import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Save, CalendarCheck, Trash2, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function TemplatesDialog({ open, onOpenChange, start, end, onApplied }) {
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/week-templates").then((r) => setTemplates(r.data));
  useEffect(() => { if (open) { load(); setName(""); } }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/week-templates", { name: name || "Semaine type", start, end });
      toast.success("Semaine enregistrée comme modèle");
      setName("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'enregistrer");
    } finally { setBusy(false); }
  };

  const apply = async (t) => {
    try {
      const res = await api.post(`/week-templates/${t.id}/apply`, { start });
      const n = res.data.created;
      toast.success(n ? `${n} repas ajoutés depuis « ${t.name} »` : "Créneaux déjà remplis");
      onOpenChange(false);
      onApplied && onApplied();
    } catch { toast.error("Impossible d'appliquer le modèle"); }
  };

  const remove = async (id) => { await api.delete(`/week-templates/${id}`); load(); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-3xl" data-testid="templates-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Semaines types</DialogTitle>
          <DialogDescription className="sr-only">Enregistrer et réutiliser des semaines modèles</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 rounded-2xl bg-[var(--at-elevated)] p-3">
          <p className="text-sm font-medium">Enregistrer la semaine affichée</p>
          <div className="flex gap-2">
            <Input data-testid="template-name-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Ex : Semaine express" className="h-11 rounded-2xl border-black/10 bg-white" />
            <Button data-testid="save-template-btn" onClick={save} disabled={busy}
              className="h-11 shrink-0 rounded-2xl px-4 text-white at-press" style={{ background: "var(--at-sage)" }}>
              <Save size={18} />
            </Button>
          </div>
        </div>

        <div className="mt-2 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Mes modèles</p>
          {templates.length === 0 && <p className="py-3 text-center text-sm" style={{ color: "var(--at-muted)" }}>Aucun modèle enregistré.</p>}
          {templates.map((t) => (
            <div key={t.id} data-testid={`template-${t.id}`} className="flex items-center gap-2 rounded-2xl border border-black/5 bg-white p-3 at-shadow">
              <LayoutGrid size={18} style={{ color: "var(--at-sage)" }} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{t.name}</p>
                <p className="text-xs" style={{ color: "var(--at-muted)" }}>{t.count} repas</p>
              </div>
              <Button data-testid={`apply-template-${t.id}`} onClick={() => apply(t)} size="sm" className="rounded-xl text-white at-press" style={{ background: "var(--at-brand)" }}>
                <CalendarCheck size={15} className="mr-1" /> Appliquer
              </Button>
              <button data-testid={`delete-template-${t.id}`} onClick={() => remove(t.id)} style={{ color: "var(--at-muted)" }}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
